<template>
  <v-form ref="form" v-model="valid" lazy-validation @submit.prevent>
    <div class="mb-6">
      <div class="subtitle1 text-primary mb-2">
        {{
          category === SecureWebGatewayCategories.URL
            ? $t("modals.editUrl.urlLabel")
            : $t("modals.editUrlGroup.urlLabel")
        }}
      </div>
      <div class="body1" v-text="localValue.name" />
    </div>

    <div class="mt-6">
      <div>
        <v-autocomplete
          v-model="localValue.labels"
          class="mt-4"
          :items="labels"
          :label="$t('network.secureWebGateway.labelsDropdownLabel')"
          :placeholder="$t('network.secureWebGateway.labelsAutocompletePlaceholder')"
          :rules="labelRules"
          variant="outlined"
          multiple
          return-object
          chips
          closable-chips
          clear-on-select
          data-testid="edit-swg-url-labels"
          item-value="id"
          item-title="name"
        >
          <template v-slot:chip="{ props, item }">
            <v-chip
              v-bind="props"
              size="default"
              :color="item.raw.color"
              close-icon="$close"
              :text="item.raw.name"
              class="label-chip"
              variant="flat"
              label
              rounded="3"
            >
              <span class="coro-label text-uppercase text-primary mr-1" v-text="item.title"> </span>
            </v-chip>
          </template>
          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props" title="">
              <div class="d-flex align-center label-popup-block">
                <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
                <span v-text="item.raw.name" />
              </div>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>
    </div>
  </v-form>
</template>

<script lang="ts">
import { defineComponent, type Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { type DeviceLabel, useDevicesSettingsStore } from "@/_store/devices-settings.module.ts";
import { storeToRefs } from "pinia";
import { SecureWebGatewayCategories } from "@/constants/network.ts";

export default defineComponent({
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:valid", "update:localValue"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const devicesSettingsStore = useDevicesSettingsStore();
    const { labels } = storeToRefs(devicesSettingsStore);

    const form = ref();
    const valid = ref(false);
    const localValue: Ref<{
      name: string;
      id: string;
      labels: DeviceLabel[];
    }> = ref({
      name: props.config.item.name || "",
      id: props.config.item.id || "",
      labels: props.config.item.labels || [],
    });

    watch(
      localValue,
      (newValue) => {
        const { id, labels } = newValue;
        emit("update:localValue", { id, labels });
      },
      { deep: true }
    );

    watch(valid, (value: boolean | null) => {
      if (value === null) {
        return;
      }

      emit("update:valid", value);
    });

    const validate = async () => {
      const { valid } = await form.value.validate();
      return valid;
    };

    const labelRules = [
      (value: DeviceLabel[]) => {
        if (!value.length) {
          return t(
            `validations.deviceLabelRequired.${props.config.item.allowed ? "allowList" : "blockList"}`
          );
        }
        return true;
      },
    ];

    return {
      form,
      valid,
      validate,
      localValue,
      labels,
      labelRules,
      category: props.config.item.category,
      SecureWebGatewayCategories,
    };
  },
});
</script>

<style scoped lang="scss">
.color-preview {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

.label-popup-block {
  padding: 4px 0;
}
</style>
